<template>
  <div class="notes_content_body">
    <div class="notes_editor_container">
      <!-- Locked Document Alert -->
      <div v-if="isActiveNoteLocked" class="locked_banner">
        <div class="locked_banner_icon">
          <i class="fa fa-lock"></i>
        </div>
        <div class="locked_banner_text">
          <strong>{{ (activeNote.edited_by && activeNote.edited_by.first_name) || '' }} {{ (activeNote.edited_by && activeNote.edited_by.last_name) || '' }}</strong>
          is currently editing this document. Read-only lock active.
        </div>
      </div>

      <!-- Locked Read-Only State -->
      <template v-if="isActiveNoteLocked">
        <input
          :value="activeNote.title"
          type="text"
          class="note_title_input readonly"
          readonly
        />
        <div class="readonly_content_area" v-html="activeNote.data"></div>
      </template>

      <!-- Active Edit Form -->
      <template v-else>
        <div class="editor_header_field">
          <input
            :value="activeNote.title"
            type="text"
            class="note_title_input"
            maxlength="255"
            placeholder="Title..."
            @input="onTitleInput"
            @keyup="$emit('lock-requested', activeNote.id)"
            @change="$emit('save-note')"
          />
        </div>
        <vue-editor
          :key="editorKey"
          ref="activeNotesEditor"
          :value="activeNote.data"
          :editor-toolbar="customToolbar"
          :config="editorOptions"
          placeholder="Description..."
          @input="onDataInput"
          @blur="$emit('save-note')"
          @focus="$emit('lock-requested', activeNote.id)"
          @ready="onEditorReady"
        />
      </template>
    </div>
  </div>
</template>

<script>
import { VueEditor } from 'vue2-editor';

export default {
  name: 'NotesEditView',

  components: { VueEditor },

  props: {
    activeNote: { type: Object, default: () => ({}) },
    isActiveNoteLocked: { type: Boolean, default: false },
    editorKey: { type: String, default: '' },
    customToolbar: { type: Array, default: () => [] },
    editorOptions: { type: Object, default: () => ({}) }
  },

  data() {
    return {
      quillInstance: null
    };
  },

  watch: {
    editorKey() {
      this.$nextTick(this.clearEditorHistory);
    }
  },

  methods: {
    onTitleInput(e) {
      this.$emit('update-active-note', { key: 'title', value: e.target.value });
      this.$emit('mark-dirty');
    },

    onDataInput(val) {
      this.$emit('update-active-note', { key: 'data', value: val });
      this.$emit('mark-dirty');
    },

    onEditorReady(quill) {
      this.quillInstance = quill || null;
      this.clearEditorHistory();
    },

    clearEditorHistory() {
      this.$nextTick(() => {
        if (this.quillInstance && this.quillInstance.history) {
          this.quillInstance.history.clear();
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
$bg-surface: #18191f;
$border-color: #262933;
$text-main: #f1f3f7;
$text-secondary: #9096a6;
$text-muted: #5e6473;
$accent-indigo: #ffd400;

.notes_content_body {
  flex: 1;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.notes_editor_container {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding:5px 20px 20px;
  overflow: hidden;
  background: #0b0c10;

  .editor_header_field {
    margin-bottom: 12px;
  }

  .note_title_input {
    width: 100%;
    background: transparent;
    border: none;
    border-bottom: 1px solid rgba(255, 255, 255, 0.02);
    font-size: 18px;
    font-weight: 600;
    color: $accent-indigo;
    padding: 8px 0 12px 0;
    outline: none;
    transition: border-color 0.2s ease;

    &:focus {
      border-bottom-color: $accent-indigo;
    }

    &.readonly {
      border-bottom-color: transparent;
      color: $text-secondary;
    }

    &::placeholder {
      color: $text-muted;
      font-weight: 400;
    }
  }
::v-deep .quillWrapper {
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: hidden;
    background: none;
    border: none !important;

    /* Floating Pill Dock Toolbar */
    .ql-toolbar.ql-snow {
      align-self: flex-start; /* change to 'center' if you want it centered */
      display: inline-flex;
      align-items: center;
      background: #121318 !important;
      border: 1px solid rgba(255, 255, 255, 0.02) !important;
      border-radius: 12px; /* modern rounded-box dock; use 9999px for full pill */
      padding: 2px 5px !important;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.45);
      gap: 0px;

      /* Divider groups */
      .ql-formats {
        display: inline-flex;
        align-items: center;
        margin: 0 4px 0 0 !important;
        padding-right: 8px;
        border-right: 1px solid rgba(255, 255, 255, 0.1);

        &:last-child {
          margin-right: 0 !important;
          padding-right: 0;
          border-right: none;
        }
      }

      /* Fix collapsed font & picker dropdowns */
      .ql-picker {
        color: #d1d5db;
        font-size: 13px;
        font-weight: 500;
        height: 26px;
        display: inline-flex;
        align-items: center;

        &.ql-font,
        &.ql-size,
        &.ql-header {
          width: auto;
          min-width: 95px;
        }

        .ql-picker-label {
          display: inline-flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          height: 100%;
          padding: 0 8px;
          border-radius: 5px;
          border: none;
          outline: none;
          color: inherit;
          transition: background 0.15s ease;

          &:hover {
            background: rgba(255, 255, 255, 0.09);

            .ql-stroke {
              stroke: #ffffff;
            }
          }

          svg {
            right: 4px;
            width: 14px;
            height: 14px;
          }
        }

        /* Fix collapsed color/background picker icons */
        &.ql-color,
        &.ql-background {
          width: 25px;
          height: 25px;

          .ql-picker-label {
            padding: 4px;
            justify-content: center;

            svg {
              position: static;
              width: 16px;
              height: 16px;
            }
          }
        }

        /* Dropdown popover list */
        .ql-picker-options {
          background-color: #17181f;
          border: 1px solid rgba(255, 255, 255, 0.12);
          border-radius: 8px;
          padding: 6px;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.5);
          margin-top: 6px;
          right: 0px;
        }

        .ql-picker-item {
          padding: 6px 12px;
          border-radius: 4px;
          color: #9ca3af;
          font-size: 13px;

          &:hover,
          &.ql-selected {
            background: #252834;
            color: #ffffff;
          }
        }
      }

      /* Toolbar Buttons (B, I, U, etc.) */
      button {
        width: 25px;
        height: 25px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: 5px;
        background: transparent;
        border: none;
        outline: none;
        padding: 0 !important;
        margin: 0 1px;
        transition: background 0.15s ease, color 0.15s ease;

        /* Fix default font icons like text B, I, U */
        color: #94a3b8;
        font-size: 14px;
        font-weight: 600;

        svg {
          width: 16px;
          height: 16px;
        }

        .ql-stroke {
          stroke-width: 1.8;
          transition: stroke 0.15s ease;
        }

        .ql-fill {
          transition: fill 0.15s ease;
        }

        /* Active pressed state (like the Hand icon in reference 2) */
        &.ql-active {
          background: #2a2c36 !important;
          color: #ffffff;

          .ql-stroke { stroke: #ffffff !important; }
          .ql-fill { fill: #ffffff !important; }
        }
      }
    }

    /* Content Area */
    .ql-container {
      flex: 1;
      border: 1px solid rgba(255, 255, 255, 0.02) !important;
      border-radius: 10px;
      font-size: 14px;
      line-height: 1.7;
      color: #e5e7eb;


      &.ql-snow {
        background: #111217 !important;
      }

      .ql-editor {
        padding: 18px 20px;
        min-height: 100%;
        &::-webkit-scrollbar {
            width: 4px;
            background: rgba(12, 13, 18, 0.9);
        }
        &::-webkit-scrollbar-thumb {
            background-color: #111217;
            border-radius: 10px
        }

        &.ql-blank::before {
          left: 20px !important;
          color: #4b5262 !important;
          font-style: normal;
        }
      }
    }
  }
}

.readonly_content_area {
  flex: 1;
  overflow-y: auto;
  line-height: 1.7;
  font-size: 14px;
  color: #cfd3dc;
  padding: 16px;
  background: $bg-surface;
  border: 1px solid $border-color;
  border-radius: 8px;
}

.locked_banner {
  background: rgba(245, 158, 11, 0.08);
  border: 1px solid rgba(245, 158, 11, 0.2);
  color: #fbbf24;
  padding: 10px 14px;
  border-radius: 8px;
  margin-bottom: 14px;
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 13px;
}
</style>